import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  createTrustDevice,
  login as apiLogin,
  verifyTotp as apiVerifyTotp,
} from '../api/auth';
import BigButton from '../components/BigButton';
import Brand from '../components/Brand';
import FormField, { TextInput } from '../components/FormField';
import { useAuthStore } from '../store/auth';

/**
 * Two-step login:
 *
 *   1. Email + password. Submitting calls /auth/login with the locally-stored
 *      trust token + UA fingerprint as headers. The backend either:
 *        (a) returns full tokens immediately — TOTP not required (either the
 *            user has TOTP off, OR the trust token + fingerprint matched and
 *            TOTP was skipped),
 *        (b) returns requires_totp=true + a temp_token — proceed to step 2.
 *
 *   2. TOTP code. Submitting calls /auth/verify-totp. On success, full tokens
 *      come back AND we mint a fresh 30-day trust-device row so the *next*
 *      login can skip step 2.
 */
export default function Login() {
  const [step, setStep] = useState('credentials'); // 'credentials' | 'totp'

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tempToken, setTempToken] = useState(null);
  const [totpCode, setTotpCode] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname || '/';

  async function onSubmitCredentials(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const data = await apiLogin({ email, password });
      if (data.requires_totp) {
        setTempToken(data.temp_token);
        setStep('totp');
      } else {
        // No TOTP needed (either disabled, or skipped via trust-device).
        useAuthStore.getState().setTokens({
          access: data.access_token,
          refresh: data.refresh_token,
          user: data.user,
        });
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          'Sign-in failed. Check your email and password.',
      );
    } finally {
      setLoading(false);
    }
  }

  async function onSubmitTotp(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const data = await apiVerifyTotp({ tempToken, code: totpCode });
      useAuthStore.getState().setTokens({
        access: data.access_token,
        refresh: data.refresh_token,
        user: data.user,
      });

      // Mint a fresh trust-device row so the next login can skip TOTP.
      // Best-effort — failure here doesn't block sign-in.
      try {
        await createTrustDevice();
      } catch (trustErr) {
        console.warn('Failed to register trust device:', trustErr);
      }

      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.detail || 'Invalid code. Try again.',
      );
    } finally {
      setLoading(false);
    }
  }

  function backToCredentials() {
    setStep('credentials');
    setTempToken(null);
    setTotpCode('');
    setError(null);
  }

  return (
    <main className="min-h-full flex flex-col px-4 pt-safe-top pb-safe-bottom">
      {/* Splash header — Brand mark stands alone (size="lg"), centred,
          with a contextual subtitle that flips between the two steps. */}
      <header className="pt-12 pb-8 flex flex-col items-center text-center">
        <Brand size="lg" variant="light" subtitle={null} className="justify-center" />
        <p className="mt-3 text-small text-gray-500">
          {step === 'credentials' ? 'Sign in to continue' : 'Two-factor verification'}
        </p>
      </header>

      {step === 'credentials' && (
        <form onSubmit={onSubmitCredentials} className="space-y-4">
          <FormField label="Email">
            <TextInput
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </FormField>

          <FormField label="Password">
            <TextInput
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </FormField>

          {error && (
            <p role="alert" className="text-small text-danger">
              {error}
            </p>
          )}

          <BigButton type="submit" loading={loading} size="lg">
            {loading ? 'Signing in…' : 'Sign in'}
          </BigButton>
        </form>
      )}

      {step === 'totp' && (
        <form onSubmit={onSubmitTotp} className="space-y-4">
          <p className="text-small text-gray-700">
            Enter the 6-digit code from your authenticator app.
          </p>

          {/* TOTP code input is intentionally taller (h-14) and uses
              text-display + tracking-widest to mimic a one-time-code field. */}
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            autoComplete="one-time-code"
            required
            maxLength={6}
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
            className="w-full h-14 px-3 border border-gray-300 rounded-md text-display text-center tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />

          {error && (
            <p role="alert" className="text-small text-danger">
              {error}
            </p>
          )}

          <BigButton
            type="submit"
            loading={loading}
            disabled={totpCode.length !== 6}
            size="lg"
          >
            {loading ? 'Verifying…' : 'Verify'}
          </BigButton>

          <button
            type="button"
            onClick={backToCredentials}
            className="w-full min-h-touch text-small text-amber-700 hover:underline"
          >
            Back to email + password
          </button>
        </form>
      )}

      <p className="mt-auto pt-8 text-caption text-gray-400 text-center">
        After your first sign-in this device will be remembered for 30 days.
      </p>
    </main>
  );
}
